import clsx from "clsx";
import { daysSinceDay, fmtMoney, fmtNum, fmtPct } from "../format";
import {
  bookingsTone,
  capture,
  CONV_RATE_HINT,
  expectedBookings,
  expectedBookingsHint,
  PAID_CAPTURE_HINT,
  paidCaptureTone,
  ratio,
  Tone,
  TONE_CLASS,
  valueTone,
  windowOf,
} from "../metrics";
import type { FleetMetricsRow } from "../types";

/**
 * The hotel page's headline row — the SAME six columns the fleet table leads
 * with, so the number an operator clicked through on is the number they land
 * on. Each tile leads with the recent window and carries the 90-day figure
 * beneath it, exactly as the table's cells do (census wtoggle geometry).
 *
 * A null is the source having no ingested day — rendered as an em-dash, never
 * as a zero (insufficient and none are not the same fact).
 */
export function HotelMetricTiles({
  metrics,
  recentDays,
  currency,
  unavailable,
  isLoading,
}: {
  metrics: FleetMetricsRow | undefined;
  recentDays: number;
  currency: string | null;
  unavailable: boolean;
  isLoading: boolean;
}) {
  const main = windowOf(metrics, "recent");
  const alt = windowOf(metrics, "full");
  const cur = metrics?.currency ?? currency;
  const expected = expectedBookings(metrics, "recent");
  const liveFor = daysSinceDay(metrics?.adsStart ?? null);
  const windowSub = `${recentDays}d · 90d below`;

  if (unavailable) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] px-4 py-3 mb-4 text-sm text-warning-dark">
        Couldn't load this hotel's metrics — the health cards below still
        render from the status read.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
      <MetricTile
        label="Clicks"
        sub={windowSub}
        main={fmtNum(main?.clicks)}
        alt={fmtNum(alt?.clicks)}
        tone={main?.clicks == null ? "zero" : ""}
        loading={isLoading}
      />
      <MetricTile
        label="Bookings"
        sub={windowSub}
        main={fmtNum(main?.conversions)}
        alt={fmtNum(alt?.conversions)}
        tone={bookingsTone(main, expected)}
        title={expectedBookingsHint(expected)}
        loading={isLoading}
      />
      <MetricTile
        label="Value"
        sub={windowSub}
        main={
          main?.conversionsValue == null
            ? "—"
            : fmtMoney(main.conversionsValue, cur)
        }
        alt={
          alt?.conversionsValue == null
            ? "—"
            : fmtMoney(alt.conversionsValue, cur)
        }
        tone={valueTone(main)}
        loading={isLoading}
      />
      <MetricTile
        label="Conv rate"
        sub={windowSub}
        main={fmtPct(ratio(main?.conversions, main?.clicks), 1)}
        alt={fmtPct(ratio(alt?.conversions, alt?.clicks), 1)}
        title={CONV_RATE_HINT}
        loading={isLoading}
      />
      <MetricTile
        label="Paid sess. / clicks"
        sub={windowSub}
        main={fmtPct(capture(main?.ga4PaidSessions, main?.clicks))}
        alt={fmtPct(capture(alt?.ga4PaidSessions, alt?.clicks))}
        tone={paidCaptureTone(main)}
        title={PAID_CAPTURE_HINT}
        loading={isLoading}
      />
      <MetricTile
        label="Live for"
        sub="since first served impression"
        main={liveFor == null ? "—" : `${liveFor}d`}
        alt={metrics?.adsStart ?? "ads-start unknown"}
        tone={liveFor == null ? "zero" : ""}
        loading={isLoading}
      />
    </div>
  );
}

function MetricTile({
  label,
  sub,
  main,
  alt,
  tone = "",
  title,
  loading,
}: {
  label: string;
  sub: string;
  main: string;
  alt: string;
  tone?: Tone;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
  loading: boolean;
}) {
  return (
    <div
      className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]"
      data-tip={title}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-[10px] text-gray-400 mb-1">{sub}</p>
      <p
        className={clsx(
          "text-2xl font-semibold tabular-nums",
          TONE_CLASS[tone],
        )}
      >
        {loading ? "…" : main}
      </p>
      <p className="text-[11px] text-gray-400 tabular-nums">
        {loading ? "" : alt}
      </p>
    </div>
  );
}
